import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from '@angular/common/http';
import { Observable, switchMap, catchError } from 'rxjs';

import { environment } from '../../../environments/environment.development';
import { DeviceTokenService } from '../services/device-token.service';

/**
 * Dokłada nagłówki tożsamości urządzenia (`X-Device-Token` + `X-Device-Id`) do
 * anonimowych żądań Privacy Center / consents.
 *
 * Zakres jest ŚCIŚLE ograniczony do /consents i /privacy/* (z wyłączeniem samego
 * /privacy/device-token, by uniknąć rekurencji). Dzięki temu CZAT pozostaje
 * nietknięty: jego strumienie to natywny EventSource (omija HttpClient w całości),
 * a wywołania HTTP idą na /events, /inquiries, /chat-presence (zero styku z tym
 * zakresem) z tokenem w query/X-Guest-Token, nie w nagłówkach urządzenia.
 */
@Injectable()
export class DeviceHeaderInterceptor implements HttpInterceptor {
  private readonly deviceToken = inject(DeviceTokenService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.shouldAttach(request.url)) {
      return next.handle(request);
    }

    return this.deviceToken.ensureDeviceIdentity().pipe(
      switchMap(({ deviceId, deviceToken }) =>
        next.handle(
          request.clone({
            setHeaders: {
              'X-Device-Token': deviceToken,
              'X-Device-Id': deviceId,
            },
          }),
        ),
      ),
      // Gdy wydanie tokenu urządzenia padnie, wyślij żądanie bez nagłówków
      // (backend w trybie przejściowym je akceptuje, dopóki DEVICE_TOKEN_REQUIRED != true).
      catchError(() => next.handle(request)),
    );
  }

  private shouldAttach(url: string): boolean {
    if (!url.startsWith(environment.apiUrl)) {
      return false;
    }
    if (url.includes('/privacy/device-token')) {
      return false;
    }
    if (url.includes('/privacy/')) {
      return true;
    }
    // Publiczne katalogi zgód (/consents/types, /consents/by-context) NIE potrzebują
    // tożsamości urządzenia — pomijamy je, by nie wymuszać preflightu z X-Device-Token.
    if (url.includes('/consents/types') || url.includes('/consents/by-context')) {
      return false;
    }
    return url.includes('/consents');
  }
}
